<script lang="ts">
import type { TableCellProperties } from "@kaleidoscope-solutions/engine-wasm"
import type { CSSProperties } from "vue"
import type { Cell, Panels } from "@/logic/GridBuilder"
import { CellDisplayKind, CellKind } from "@kaleidoscope-solutions/engine-wasm"
import { useElementBounding, useElementVisibility, useResizeObserver, useScroll } from "@vueuse/core"
import { computed, nextTick, onBeforeUnmount, ref, toRef, useTemplateRef, watch } from "vue"
import { setCellClassByCell, uniqueCellKey } from "@/utils/table"

export interface GridProps {
  name: string
  panels: Panels
  selectedCell?: Cell
  // Set the cell key to use to display the content of the cell
  valueKey?: (keyof TableCellProperties)
  pageScrollRef?: HTMLElement
}
</script>

<script lang="ts" setup>
interface Emits {
  (event: "cellSelected", cell: Cell): void
}

const props = withDefaults(defineProps<GridProps>(), {})

const emit = defineEmits<Emits>()
const panels = toRef(props, "panels")
const valueKey = toRef(props, "valueKey", "formattedValue")

function selectedCellCssClasses(selectedCell: Cell, cell: Cell): string[] {
  const classes = []
  const sameRow = selectedCell.row === cell.row
  const sameColumn = selectedCell.column === cell.column
  const isColumnHeader = cell.displayKind === CellDisplayKind.ColumnHeader
  const colSpan = cell?.colspan ?? 0
  const inColSpanRange =
    selectedCell.column !== undefined &&
    selectedCell.column >= cell.column &&
    selectedCell.column < cell.column + colSpan

  const colRangeLast =
    selectedCell.column !== undefined && cell.column === selectedCell.column + colSpan - 1
  const rowSpan = cell?.rowspan ?? 0

  const inRowSpanRange =
    selectedCell.row !== undefined &&
    selectedCell.row >= cell.row &&
    selectedCell.row < cell.row + (cell?.rowspan ?? 0)

  const rowRangeLast =
    selectedCell.row !== undefined && cell.row === selectedCell.row + rowSpan - 1

  // Select the current cell.
  if (sameRow && sameColumn) {
    classes.push("selected-cell--cell")
  }

  // not nested & Select the current column.
  if (colSpan === 1 && sameColumn) {
    classes.push("selected-cell--column")
  }

  // for nested cells
  if (inColSpanRange) {
    classes.push("selected-cell--nested--col")

    // start of range
    if (sameColumn) {
      classes.push("selected-cell--nested--col-start")
    }
    // end of range
    if (colRangeLast) {
      classes.push("selected-cell--nested--col-end")
    }
  }

  // for nested ROWS
  if (inRowSpanRange) {
    classes.push("selected-cell--nested--row")

    // start of range
    if (sameRow) {
      classes.push("selected-cell--nested--row-start")
    }
    // end of range
    if (rowRangeLast) {
      classes.push("selected-cell--nested--row-end")
    }
  }

  // Style column headers different.
  if (sameColumn && isColumnHeader) {
    classes.push("selected-cell--column-header")
  }

  // If body row is selected.
  if (sameRow) {
    classes.push("selected-cell--row")
  }

  return classes
}

function cellClassMapper(cell: Cell) {
  // Set a class based on the display kind.
  const cellClasses = [`cell--${CellDisplayKind[cell.displayKind]}`]

  if (cell.cellKind !== undefined) {
    cellClasses.push(`cell--${CellKind[cell.cellKind]}`)
  }

  if (cell.valueType) {
    cellClasses.push(`cell--vt-${cell.valueType}`)
  }

  if (cell.emptyRelationship) {
    cellClasses.push("cell--emptyRelationship")
  }

  // Set class for read only.
  if (cell.readOnly) {
    cellClasses.push("cell--readOnly")
  }

  if (props.selectedCell) {
    cellClasses.push(...selectedCellCssClasses(props.selectedCell, cell))
  }

  cellClasses.push(...setCellClassByCell(cell))

  return cellClasses.join(" ")
}

function cellStyle(cell: Cell) {
  const style: CSSProperties = {}

  if (cell.rowspan > 1) {
    style.gridRow = `span ${cell.rowspan}`
  }

  if (cell.colspan > 1) {
    style.gridColumn = `span ${cell.colspan}`
  }

  return style
}

function handleCellClick(cell: Cell) {
  emit("cellSelected", cell)
}
const tableRef = useTemplateRef("tableRef")
const selectedCellRef = useTemplateRef("selectedCellRef")
const columnsRef = useTemplateRef("columnsRef")
const bodyRef = useTemplateRef("bodyRef")
const customScrollbarRef = useTemplateRef("customScrollbarRef")

const columnScroll = useScroll(columnsRef)
const bodyScroll = useScroll(bodyRef)
const customScrollbarScroll = useScroll(customScrollbarRef)

let isSyncingColumns = false
let isSyncingBody = false
let isSyncingCustom = false

// Sync body when column headers scrolls
watch(columnScroll.x, (newX) => {
  if (isSyncingColumns) {
    return
  }
  syncBodyScroll(newX)
  syncCustomScroll(newX)
})

// Sync column headers when body scrolls
watch(bodyScroll.x, (newX) => {
  if (isSyncingBody) {
    return
  }
  syncColumnsScroll(newX)
  syncCustomScroll(newX)
})

// Sync column headers and body when custom scrollbar scrolls
watch(customScrollbarScroll.x, (newX) => {
  if (isSyncingCustom) {
    return
  }

  syncBodyScroll(newX)
  syncColumnsScroll(newX)
})

function syncBodyScroll(newX: number) {
  if (bodyRef.value === null) {
    return
  }

  isSyncingBody = true
  bodyRef.value.scrollLeft = newX
  isSyncingBody = false

  positionSelectedCellRef()
}

function syncColumnsScroll(newX: number) {
  if (columnsRef.value === null) {
    return
  }

  isSyncingColumns = true
  columnsRef.value.scrollLeft = newX
  isSyncingColumns = false
}

function syncCustomScroll(newX: number) {
  if (customScrollbarRef.value === null) {
    return
  }

  isSyncingCustom = true
  customScrollbarRef.value.scrollLeft = newX
  isSyncingCustom = false
}

/**
 * Resizing column logic
 */

const startX = ref(0)
const startWidth = ref(0)
const resizingIndex = ref<number | null>(null)
const currentPanel = ref<"Columns" | "Corner" | null>()

function getColumnWidths(cells: Cell[][]) {
  return (cells[0] ?? []).map((cell) => cell.columnWidth ?? 0)
}

function getViewHolderWidth(): number {
  const el = document.getElementById("viewHolderRef")
  return (el ? el.clientWidth : window.innerWidth)
}

function clampCornerWidths(widths: number[]): number[] {
  // get 75% of the view holder width
  const maxWidth = getViewHolderWidth() * 0.75
  const total = widths.reduce((a, b) => a + b, 0)
  if (total <= maxWidth) {
    return widths
  }
  // Only scale widths that are larger than their proportional share
  const proportional = maxWidth / widths.length
  const scaled = widths.slice()
  const excess = total - maxWidth

  // Find indices of widths that are above proportional
  const largeIndices = widths.map((w, i) => w > proportional ? i : -1).filter((i) => i !== -1)
  const largeTotal = largeIndices.reduce((sum, i) => sum + widths[i], 0)
  if (largeTotal === 0) {
    // fallback: divide evenly
    return widths.map(() => Math.floor(proportional))
  }
  // Scale only the large widths
  largeIndices.forEach((i) => {
    const w = widths[i]
    const scaledW = Math.floor(w - (excess * (w / largeTotal)))
    scaled[i] = Math.max(scaledW, proportional)
  })
  return scaled
}

const cornerWidths = ref<number[]>(clampCornerWidths(getColumnWidths(panels.value.Corner)))
const columnWidths = ref<number[]>(getColumnWidths(panels.value.Columns))

watch([panels, tableRef], () => {
  cornerWidths.value = clampCornerWidths(getColumnWidths(panels.value.Corner))
  columnWidths.value = getColumnWidths(panels.value.Columns)
})

const cornerGridTemplate = computed(() => cornerWidths.value.map((w) => `${w}px`).join(" "))
const columnsGridTemplate = computed(() => columnWidths.value.map((w) => `${w}px`).join(" "))

function startResizing(panel: "Columns" | "Corner", index: number, event: MouseEvent) {
  resizingIndex.value = index
  startX.value = event.clientX
  currentPanel.value = panel

  if (currentPanel.value === "Corner") {
    startWidth.value = cornerWidths.value[index]
  } else {
    startWidth.value = columnWidths.value[index]
  }

  document.addEventListener("mousemove", onMouseMove)
  document.addEventListener("mouseup", stopResizing)
}

function onMouseMove(event: MouseEvent) {
  if (resizingIndex.value === null) {
    return
  }

  const dx = event.clientX - startX.value
  const newWidth = Math.max(50, startWidth.value + dx)

  if (currentPanel.value === "Corner") {
    cornerWidths.value[resizingIndex.value] = newWidth
  } else {
    columnWidths.value[resizingIndex.value] = newWidth
  }
}

function stopResizing() {
  resizingIndex.value = null
  currentPanel.value = null
  document.removeEventListener("mousemove", onMouseMove)
  document.removeEventListener("mouseup", stopResizing)
}

// sticky horizontal scroll bar
const isTableBodyVisible = useElementVisibility(bodyRef)
const bodyRefBounding = useElementBounding(bodyRef)

const barPosition = computed<CSSProperties>(() => {
  const styles: CSSProperties = {
    left: "0",
    width: "0",
    position: "absolute",
    bottom: "0",
    zIndex: "50",
  }

  if (isStickyScrollbar.value) {
    styles.left = `${bodyRefBounding.left.value}px`
    styles.position = "fixed"
    styles.width = `${bodyRefBounding?.width.value}px`
  } else {
    styles.left = "0"
    styles.position = "absolute"
    styles.width = "100%"
  }

  return styles
})

const showCustomScrollbar = ref(false)

useResizeObserver(bodyRef, checkIfShowScrollbar)

function checkIfShowScrollbar() {
  if (!bodyRef.value) {
    return
  }

  const hasOverflow = bodyRef.value.scrollWidth > bodyRef.value.clientWidth
  showCustomScrollbar.value = hasOverflow
}

// Only sticky if table is visible and its bottom is at or below viewport
const isStickyScrollbar = computed(() => {
  return isTableBodyVisible.value && bodyRefBounding.bottom.value >= window.innerHeight
})

// keyboard nav of cells check if its in view
function checkInView(
  target: HTMLElement,
  parent: HTMLElement,
  axis: "x" | "y",
  opts: { margin: number; headerOffset?: number } = { margin: 24 }
) {
  const { margin, headerOffset = 0 } = opts
  const t = target.getBoundingClientRect()
  const p = parent.getBoundingClientRect()

  if (axis === "x") {
    // If target is left of parent's visible area (including margin) -> scroll left
    if (t.left < p.left + margin) {
      parent.scrollLeft += t.left - (p.left + margin)
    } else if (t.right > p.right - margin) {
      // If target is right of parent's visible area -> scroll right
      parent.scrollLeft += t.right - (p.right - margin)
    }
  } else {
    // Vertical axis: respect a header offset so top can be visible below sticky header
    const visibleTop = p.top + margin + headerOffset
    const visibleBottom = p.bottom - margin

    if (t.top < visibleTop) {
      parent.scrollTop += t.top - visibleTop
    } else if (t.bottom > visibleBottom) {
      parent.scrollTop += t.bottom - visibleBottom
    }
  }
}

async function scrollToCell(cell: Cell, margin = 24) {
  if (tableRef.value === null) {
    return
  }

  await nextTick()

  const targetEl: HTMLElement | null = tableRef.value.querySelector(`[data-cell-row="${cell.row}"][data-cell-column="${cell.column}"]`)
  if (!targetEl) {
    return
  }

  // compute header offset from columnsRef (columns panel mirrors header height)
  const headerOffset = columnsRef?.value ? columnsRef.value.getBoundingClientRect().height : 0

  let horizParent: HTMLElement | null = null

  if (cell.panel === "Columns") {
    horizParent = columnsRef.value
  } else if (cell.panel === "Body") {
    horizParent = bodyRef.value
  }

  // Horizontal: Scroll overflow body or column
  if (horizParent) {
    checkInView(targetEl, horizParent, "x", { margin })
  }

  // Vertical: use explicit pageScrollRef for vertical sticky column
  if (props.pageScrollRef) {
    checkInView(targetEl, props.pageScrollRef, "y", { margin, headerOffset })
  }
}
defineExpose({ scrollToCell }) // expose to parent component WidgetTableGrid

onBeforeUnmount(() => {
  stopResizing()
})

// Find cell that spans over the given cell if the cell is hidden by span
function findSpanningCell(cell: Cell): Cell | undefined {
  return Object.values(panels.value).flat().flat().find((c) => {
    if (c.hiddenBySpan) {
      return false
    }

    const inColSpanRange =
      cell.column >= c.column &&
      cell.column < c.column + c.colspan
    const inRowSpanRange =
      cell.row >= c.row &&
      cell.row < c.row + c.rowspan
    return inColSpanRange && inRowSpanRange
  })
}

// Position the selected cell ref over the selected cell
function positionSelectedCellRef() {
  if (!selectedCellRef.value || !tableRef.value) {
    return
  }

  if (props.selectedCell === undefined) {
    selectedCellRef.value.style.display = "none"
    return
  }

  let selectedCell = props.selectedCell

  // Find cell that hides the selected cell by span
  if (selectedCell.hiddenBySpan) {
    // find cell that spans over this cell
    const parentCell = findSpanningCell(props.selectedCell)
    if (parentCell) {
      selectedCell = parentCell
    }
  }

  const cellEl = tableRef.value?.querySelector(`[data-test="cell-${selectedCell.row}-${selectedCell.column}"] .cell-inner`)
  if (!cellEl) {
    selectedCellRef.value.style.display = "none"
    return
  }
  const tableCellRect = cellEl.getBoundingClientRect()
  const tableRect = tableRef.value.getBoundingClientRect()

  // Set the display as we might have previously set it to none
  selectedCellRef.value.style.display = "block"
  selectedCellRef.value.style.left = `${tableCellRect.left - tableRect.left}px`
  selectedCellRef.value.style.top = `${tableCellRect.top - tableRect.top}px`
  selectedCellRef.value.style.width = `${tableCellRect.width}px`
  selectedCellRef.value.style.height = `${tableCellRect.height}px`
}

watch(() => props.selectedCell, () => {
  nextTick(positionSelectedCellRef)
})
</script>

<template>
  <div
    ref="tableRef"
    class="relative flex w-full flex-col text-sm"
  >
    <div
      ref="selectedCellRef"
      class="pointer-events-none absolute z-50 rounded-lg border-teal-700 ring-2 ring-teal-700"
      style="display: none"
    />
    <div class="sticky top-0 z-10 flex w-full overflow-hidden">
      <div
        class="grid"
        :style="{ gridTemplateColumns: cornerGridTemplate }"
      >
        <template
          v-for="(row, rowIndex) in panels.Corner"
          :key="rowIndex"
        >
          <template
            v-for="(cell, cellIndex) in row"
            :key="uniqueCellKey(cell)"
          >
            <div
              v-if="!cell.hiddenBySpan"
              :style="cellStyle(cell)"
              class="group/cell relative"
              :class="cellClassMapper(cell)"
              :data-test="`cell-${cell.row}-${cell.column}`"
              :data-cell-row="cell.row"
              :data-cell-column="cell.column"
              @click="handleCellClick(cell)"
            >
              <div
                v-if="cell.displayKind !== CellDisplayKind.Gap"
                class="cell-resizer"
                @mousedown="startResizing('Corner', cellIndex, $event)"
              />
              <template v-if="cell.displayKind === CellDisplayKind.Gap" />
              <template
                v-else-if="cell.displayKind === CellDisplayKind.SummaryCell"
              >
                <slot
                  name="summary"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>
              <template
                v-else-if="cell.displayKind === CellDisplayKind.GroupTypeLabel"
              >
                <slot
                  name="group-label"
                  :cell="cell"
                >
                  <div class="px-2 pt-1">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template
                v-else-if="cell.displayKind === CellDisplayKind.GroupColumnHeader"
              >
                <slot
                  name="group-column-header"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template
                v-else-if="cell.displayKind === CellDisplayKind.GroupRowHeader"
              >
                <slot
                  name="group-row-header"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template
                v-else-if="cell.displayKind === CellDisplayKind.ColumnHeader"
              >
                <slot
                  name="column-header"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template v-else>
                <slot :cell="cell">
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>
            </div>
          </template>
        </template>
      </div>

      <div
        ref="columnsRef"
        class="scrollbar-none w-full overflow-x-scroll"
      >
        <div
          class="grid"
          :style="{ gridTemplateColumns: columnsGridTemplate }"
        >
          <template
            v-for="(row, rowIndex) in panels.Columns"
            :key="rowIndex"
          >
            <template
              v-for="(cell, cellIndex) in row"
              :key="uniqueCellKey(cell)"
            >
              <div
                v-if="!cell.hiddenBySpan"
                :style="cellStyle(cell)"
                class="group/cell relative"
                :class="cellClassMapper(cell)"
                :data-test="`cell-${cell.row}-${cell.column}`"
                :data-cell-row="cell.row"
                :data-cell-column="cell.column"
                @click="handleCellClick(cell)"
              >
                <div
                  v-if="cell.displayKind !== CellDisplayKind.Gap"
                  class="cell-resizer"
                  @mousedown="startResizing('Columns', cellIndex, $event)"
                />
                <template v-if="cell.displayKind === CellDisplayKind.Gap" />
                <template
                  v-else-if="cell.displayKind === CellDisplayKind.SummaryCell"
                >
                  <slot
                    name="summary"
                    :cell="cell"
                  >
                    <div class="px-2 py-1.5">
                      {{ cell[valueKey] }}
                    </div>
                  </slot>
                </template>
                <template
                  v-else-if="cell.displayKind === CellDisplayKind.GroupTypeLabel"
                >
                  <slot
                    name="group-label"
                    :cell="cell"
                  >
                    <div class="px-2 pt-1">
                      {{ cell[valueKey] }}
                    </div>
                  </slot>
                </template>

                <template
                  v-else-if="cell.displayKind === CellDisplayKind.GroupColumnHeader"
                >
                  <slot
                    name="group-column-header"
                    :cell="cell"
                  >
                    <div class="px-2 py-1.5">
                      {{ cell[valueKey] }}
                    </div>
                  </slot>
                </template>

                <template
                  v-else-if="cell.displayKind === CellDisplayKind.GroupRowHeader"
                >
                  <slot
                    name="group-row-header"
                    :cell="cell"
                  >
                    <div class="px-2 py-1.5">
                      {{ cell[valueKey] }}
                    </div>
                  </slot>
                </template>

                <template
                  v-else-if="cell.displayKind === CellDisplayKind.ColumnHeader"
                >
                  <slot
                    name="column-header"
                    :cell="cell"
                  >
                    <div class="px-2 py-1.5">
                      {{ cell[valueKey] }}
                    </div>
                  </slot>
                </template>

                <template v-else>
                  <slot :cell="cell">
                    <div class="px-2 py-1.5">
                      {{ cell[valueKey] }}
                    </div>
                  </slot>
                </template>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
    <div class="flex w-full overflow-hidden">
      <div
        class="grid pb-4"
        :style="{ gridTemplateColumns: cornerGridTemplate }"
      >
        <template
          v-for="(row, rowIndex) in panels.Rows"
          :key="rowIndex"
        >
          <template
            v-for="cell in row"
            :key="uniqueCellKey(cell)"
          >
            <div
              v-if="!cell.hiddenBySpan"
              :style="cellStyle(cell)"
              class="group/cell"
              :class="cellClassMapper(cell)"
              :data-test="`cell-${cell.row}-${cell.column}`"
              :data-cell-row="cell.row"
              :data-cell-column="cell.column"
              @click="handleCellClick(cell)"
            >
              <template v-if="cell.displayKind === CellDisplayKind.Gap" />
              <template
                v-else-if="cell.displayKind === CellDisplayKind.SummaryCell"
              >
                <slot
                  name="summary"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>
              <template
                v-else-if="cell.displayKind === CellDisplayKind.GroupTypeLabel"
              >
                <slot
                  name="group-label"
                  :cell="cell"
                >
                  <div class="px-2 pt-1">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template
                v-else-if="cell.displayKind === CellDisplayKind.GroupColumnHeader"
              >
                <slot
                  name="group-column-header"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template
                v-else-if="cell.displayKind === CellDisplayKind.GroupRowHeader"
              >
                <slot
                  name="group-row-header"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template
                v-else-if="cell.displayKind === CellDisplayKind.ColumnHeader"
              >
                <slot
                  name="column-header"
                  :cell="cell"
                >
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>

              <template v-else>
                <slot :cell="cell">
                  <div class="px-2 py-1.5">
                    {{ cell[valueKey] }}
                  </div>
                </slot>
              </template>
            </div>
          </template>
        </template>
      </div>
      <div class="relative w-full overflow-hidden">
        <div
          ref="bodyRef"
          class="scrollbar-none w-full overflow-x-scroll"
        >
          <div
            class="grid"
            :style="{ gridTemplateColumns: columnsGridTemplate }"
          >
            <template
              v-for="(row, rowIndex) in panels.Body"
              :key="rowIndex"
            >
              <template
                v-for="cell in row"
                :key="uniqueCellKey(cell)"
              >
                <div
                  v-if="!cell.hiddenBySpan"
                  :style="cellStyle(cell)"
                  class="group/cell"
                  :class="cellClassMapper(cell)"
                  :data-test="`cell-${cell.row}-${cell.column}`"
                  :data-cell-row="cell.row"
                  :data-cell-column="cell.column"
                  @click="handleCellClick(cell)"
                >
                  <template v-if="cell.displayKind === CellDisplayKind.Gap" />
                  <template
                    v-else-if="cell.displayKind === CellDisplayKind.SummaryCell"
                  >
                    <slot
                      name="summary"
                      :cell="cell"
                    >
                      <div class="px-2 py-1.5">
                        {{ cell[valueKey] }}
                      </div>
                    </slot>
                  </template>
                  <template
                    v-else-if="cell.displayKind === CellDisplayKind.GroupTypeLabel"
                  >
                    <slot
                      name="group-label"
                      :cell="cell"
                    >
                      <div class="px-2 pt-1">
                        {{ cell[valueKey] }}
                      </div>
                    </slot>
                  </template>

                  <template
                    v-else-if="cell.displayKind === CellDisplayKind.GroupColumnHeader"
                  >
                    <slot
                      name="group-column-header"
                      :cell="cell"
                    >
                      <div class="px-2 py-1.5">
                        {{ cell[valueKey] }}
                      </div>
                    </slot>
                  </template>

                  <template
                    v-else-if="cell.displayKind === CellDisplayKind.GroupRowHeader"
                  >
                    <slot
                      name="group-row-header"
                      :cell="cell"
                    >
                      <div class="px-2 py-1.5">
                        {{ cell[valueKey] }}
                      </div>
                    </slot>
                  </template>

                  <template
                    v-else-if="cell.displayKind === CellDisplayKind.ColumnHeader"
                  >
                    <slot
                      name="column-header"
                      :cell="cell"
                    >
                      <div class="px-2 py-1.5">
                        {{ cell[valueKey] }}
                      </div>
                    </slot>
                  </template>

                  <template v-else>
                    <slot :cell="cell">
                      <div class="px-2 py-1.5">
                        {{ cell[valueKey] }}
                      </div>
                    </slot>
                  </template>
                </div>
              </template>
            </template>
          </div>
        </div>
        <!-- Custom horizontal scrollbar at the bottom of the viewport -->
        <div
          v-if="showCustomScrollbar"
          ref="customScrollbarRef"
          class="scrollbar-thin h-6 w-full overflow-x-scroll"
          :style="barPosition"
        >
          <div :style="{ width: `${bodyRef?.scrollWidth}px`, height: '1px' }" />
        </div>
      </div>
    </div>
  </div>
</template>
